import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import { SectionVisibility } from 'app/pages/programs/channels/Channels.constants';
import PropTypes from 'prop-types';

import { SECTION_TO_HIDDEN_TAB_NAMES } from 'constants/pathToSubmission';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import { selectActiveChannelConfig } from 'store/loanApplications/selectors';
import { selectLoanApplication } from 'store/loanApplications/selectors/loanApplication.selectors';

import { buildShortcutMap } from '../UniversalSearch/builders/navigation.utils';

import LoanNavigationTab from './LoanNavigationTab';
import { containerStyles } from './LoanNavigationTabs.styles';
import { LoanNavigationTabsTestIds } from './LoanNavigationTabs.testIds';
import { buildFilteredTabs } from './LoanNavigationTabs.utils';

const deriveChannelVisibilityTabNames = (channelConfig, visibility) => {
  if (!channelConfig?.sectionVisibilities?.length) {
    return [];
  }

  return channelConfig.sectionVisibilities
    .filter(entry => entry.visibility === visibility)
    .flatMap(entry => SECTION_TO_HIDDEN_TAB_NAMES[entry.section] ?? []);
};

const LoanNavigationTabs = ({
  availableTabs,
  applicationId,
  isModifierHeld = false,
  shortcutPrefix,
}) => {
  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const { tabNameToShortcut } = useMemo(() => buildShortcutMap({ availableTabs }), [availableTabs]);

  const lockedPageIds = useSelector(
    state => selectLoanApplication(state).lockedFields.lockedPageIds,
  );
  const channelConfig = useSelector(selectActiveChannelConfig);

  const channelHiddenTabNames = useMemo(
    () => deriveChannelVisibilityTabNames(channelConfig, SectionVisibility.HIDDEN),
    [channelConfig],
  );

  const channelReadOnlyTabNames = useMemo(
    () => deriveChannelVisibilityTabNames(channelConfig, SectionVisibility.READ_ONLY),
    [channelConfig],
  );

  const filteredTabs = useMemo(
    () =>
      buildFilteredTabs(
        availableTabs,
        lockedPageIds,
        channelHiddenTabNames,
        channelReadOnlyTabNames,
      ),
    [availableTabs, lockedPageIds, channelHiddenTabNames, channelReadOnlyTabNames],
  );

  return (
    <>
      <TruncatedProvider />
      <Box
        data-testid={LoanNavigationTabsTestIds.LOAN_MENU}
        sx={containerStyles}
        onMouseLeave={onResetTooltipState}
        onMouseOver={onMouseOver}
      >
        {filteredTabs.map(([tabKey, tabConfig], index, array) => (
          <LoanNavigationTab
            applicationId={applicationId}
            isLastChild={index === array.length - 1}
            isModifierHeld={isModifierHeld}
            key={index}
            shortcutPrefix={shortcutPrefix}
            tabConfig={tabConfig}
            tabKey={tabKey}
            tabNameToShortcut={tabNameToShortcut}
          />
        ))}
      </Box>
    </>
  );
};

LoanNavigationTabs.propTypes = {
  availableTabs: PropTypes.object.isRequired,
  applicationId: PropTypes.string.isRequired,
  isModifierHeld: PropTypes.bool,
  shortcutPrefix: PropTypes.string,
};

export default memo(LoanNavigationTabs);
